/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/capabilities/compliance-assessment/routes.tsx
 * layer: web
 * area: compliance
 * kind: feature
 * web_root: features
 * relpath: web/src/capabilities/compliance-assessment/routes.tsx
 * filename: routes.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

/**
 * Sprint 338: Compliance Routes
 * Route configuration for compliance feature
 */

import { lazy, Suspense } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import {
  ComplianceDashboardSkeleton,
  ControlListSkeleton,
  ControlDetailSkeleton,
} from './components/ComplianceSkeletons';
// Attack Sim Create + Detail are owned by the security-testing capability.
import { AttackSimCreate, AttackSimDetail } from '@/capabilities/security-testing';

// Lazy load pages for code splitting
const ComplianceDashboard = lazy(() => import('./pages/ComplianceDashboard'));
const ControlListPage = lazy(() => import('./pages/ControlListPage'));
const ControlDetailPage = lazy(() => import('./pages/ControlDetailPage'));
const M365ScannerPage = lazy(() => import('./pages/M365ScannerPage'));
const RemediationPage = lazy(() => import('./pages/RemediationPage'));
// SMB1001 verification: Hyperion evidence vs CloudCapsule + fix-once packages.
const SMB1001Page = lazy(() => import('./pages/SMB1001Page'));

const DriftMonitorPage = lazy(() => import('./pages/DriftMonitorPage'));

const ReportsPage = lazy(() => import('./pages/ReportsPage'));

// UXD prompt 07 Tab 7 — Audits absorbed into the compliance cluster.
const AuditsTab = lazy(() => import('./pages/AuditsTab'));

// UXD prompt 07 Tab 6 — Attack Sim consolidated into the compliance cluster.
const AttackSimTab = lazy(() => import('./pages/AttackSimTab'));

// UXD prompt 07 Tab 8 — Service Intelligence (suggestion engine + Observe) absorbed.
// Absorbs the standalone /observe page (ObserveImprove) as its Observe view.
const ServiceIntelligenceInsightsTab = lazy(() => import('./pages/ServiceIntelligenceInsightsTab'));

// CloudKick (M365 tenant scaffolding via Graph baseline configs) is not
// in the UXD compliance design but stays accessible by direct URL. It's
// a deploy-templates workflow, distinct from the scan/assess loop.
const CloudKickPage = lazy(() => import('./pages/CloudKickPage'));

export const AuditEditor = lazy(() =>
  import('./pages/audit/AuditEditor').then((module) => ({ default: module.AuditEditor }))
);

export const DeadlineReadiness = lazy(() =>
  import('./pages/assessor/DeadlineReadiness').then((module) => ({
    default: module.DeadlineReadiness,
  }))
);

// SecureScore content has been absorbed into the M365Scanner page —
// the Posture chapter + hero-score drawer renders the same data with
// the new chrome. The /compliance/secure-score URL stays alive as a
// redirect so external deep-links and the /m365/secure-score legacy
// alias keep working.

/**
 * Compliance feature routes
 */
export const complianceRoutes: RouteObject[] = [
  {
    index: true,
    element: (
      <Suspense fallback={<ComplianceDashboardSkeleton />}>
        <ComplianceDashboard />
      </Suspense>
    ),
  },
  {
    path: 'controls',
    element: (
      <Suspense fallback={<ControlListSkeleton />}>
        <ControlListPage />
      </Suspense>
    ),
  },
  {
    path: 'controls/:controlId',
    element: (
      <Suspense fallback={<ControlDetailSkeleton />}>
        <ControlDetailPage />
      </Suspense>
    ),
  },
  {
    path: 'remediation',
    element: (
      <Suspense fallback={<ControlListSkeleton />}>
        <RemediationPage />
      </Suspense>
    ),
  },
  {
    path: 'smb1001',
    element: (
      <Suspense fallback={<ControlListSkeleton />}>
        <SMB1001Page />
      </Suspense>
    ),
  },
  {
    path: 'scanner',
    element: (
      <Suspense fallback={<ComplianceDashboardSkeleton />}>
        <M365ScannerPage />
      </Suspense>
    ),
  },
  {
    path: 'drift',
    element: (
      <Suspense fallback={<ComplianceDashboardSkeleton />}>
        <DriftMonitorPage />
      </Suspense>
    ),
  },
  {
    path: 'reports',
    element: (
      <Suspense fallback={<ComplianceDashboardSkeleton />}>
        <ReportsPage />
      </Suspense>
    ),
  },
  {
    path: 'audits',
    element: (
      <Suspense fallback={<ControlListSkeleton />}>
        <AuditsTab />
      </Suspense>
    ),
  },
  {
    path: 'attacksim',
    element: (
      <Suspense fallback={<ComplianceDashboardSkeleton />}>
        <AttackSimTab />
      </Suspense>
    ),
  },
  {
    path: 'attacksim/new',
    element: (
      <Suspense fallback={<ControlListSkeleton />}>
        <AttackSimCreate />
      </Suspense>
    ),
  },
  {
    path: 'attacksim/:simId',
    element: (
      <Suspense fallback={<ControlListSkeleton />}>
        <AttackSimDetail />
      </Suspense>
    ),
  },
  {
    path: 'service-intelligence',
    element: (
      <Suspense fallback={<ComplianceDashboardSkeleton />}>
        <ServiceIntelligenceInsightsTab />
      </Suspense>
    ),
  },
  {
    // Absorbed into M365Scanner (Posture chapter + hero-score drawer).
    // Kept as a redirect so legacy secure-score links
    // forwards still land users somewhere useful.
    path: 'secure-score',
    element: <Navigate to="/portfolio/compliance/scanner" replace />,
  },
  {
    path: 'cloudkick',
    element: (
      <Suspense fallback={<ComplianceDashboardSkeleton />}>
        <CloudKickPage />
      </Suspense>
    ),
  },
];

export default complianceRoutes;
